/*
 * Alpine.js Component Patterns
 * Replaces Bootstrap JS interactive components
 */

// Dropdown component
function dropdown() {
    return {
        open: false,
        toggle() {
            this.open = !this.open;
        },
        close() {
            this.open = false;
        }
    };
}

// Modal component
function modal(name) {
    return {
        show: false,
        name: name || 'modal',
        open() {
            this.show = true;
            document.body.style.overflow = 'hidden';
        },
        close() {
            this.show = false;
            document.body.style.overflow = '';
        }
    };
}

// Attachment lightbox: the image attachments of one case or claim, viewed one at a time.
// `images` is [{ id, name, url, download, meta }] as emitted by AttachmentListViewModel.
// openById answers whether it holds the picture, so a picture in the description can ask
// (attachment-image-open below) and fall back to its own link when no gallery has it.
function attachmentGallery(images) {
    return {
        images: images || [],
        show: false,
        index: 0,
        get current() {
            return this.images.length ? this.images[this.index] : null;
        },
        openById(id) {
            var i = this.images.findIndex(function (m) { return m.id === id; });
            if (i < 0) return false;
            this.index = i;
            this.show = true;
            document.body.style.overflow = 'hidden';
            return true;
        },
        close() {
            if (!this.show) return;
            this.show = false;
            document.body.style.overflow = '';
        },
        prev() {
            if (!this.show || this.images.length < 2) return;
            this.index = (this.index - 1 + this.images.length) % this.images.length;
        },
        next() {
            if (!this.show || this.images.length < 2) return;
            this.index = (this.index + 1) % this.images.length;
        }
    };
}

// A picture shown in a case or claim description (CaseDescriptionRenderer) opens in the page's
// attachment lightbox: a plain click asks every gallery on the page, through a window event,
// to open that attachment, and follows the picture's own link only when none did. Ctrl-, shift-
// and middle-clicks keep the browser's own new-tab behaviour.
document.addEventListener('click', function (e) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || !e.target.closest) return;
    var link = e.target.closest('.case-description a[data-attachment-image]');
    if (!link) return;
    var detail = { id: link.getAttribute('data-attachment-image'), opened: false };
    window.dispatchEvent(new CustomEvent('attachment-image-open', { detail: detail }));
    if (detail.opened) e.preventDefault();
});

// Offcanvas/panel component
function offcanvas() {
    return {
        open: false,
        show() {
            this.open = true;
            document.body.style.overflow = 'hidden';
        },
        hide() {
            this.open = false;
            document.body.style.overflow = '';
        }
    };
}

// Dismissible alert
function dismissAlert() {
    return {
        visible: true,
        dismiss() {
            this.visible = false;
        }
    };
}

// Tooltip component
function tooltip() {
    return {
        show: false
    };
}

// Collapsible section
function collapsible(initialOpen) {
    return {
        expanded: initialOpen || false,
        toggle() {
            this.expanded = !this.expanded;
        }
    };
}

// Lazy-loaded Recent Activity card. The homepage renders immediately with a
// spinner; this component fetches a server-rendered partial after mount.
// A minimum visible-loading duration keeps the spinner perceivable on fast
// responses (localhost requests can finish in <20 ms, faster than one
// animation frame).
function recentActivity(url) {
    return {
        url: url,
        loading: false,
        error: null,
        html: '',
        async load() {
            if (this.loading) return;
            this.loading = true;
            this.error = null;
            const minVisible = new Promise(r => setTimeout(r, 300));
            try {
                const res = await fetch(this.url, {
                    credentials: 'same-origin',
                    headers: { 'X-Requested-With': 'fetch' }
                });
                // A session that expired mid-visit answers with a redirect to sign-in; the
                // browser follows it and hands us the sign-in page as a 200. Reload so the
                // user sees the sign-in page itself instead of it nested inside a card.
                if (res.redirected && /\/signin/i.test(res.url)) {
                    window.location.reload();
                    return;
                }
                const type = (res.headers.get('content-type') || '').toLowerCase();
                if (!res.ok) {
                    throw new Error(await portalErrorMessage(res, type));
                }
                if (type.indexOf('text/html') === -1) {
                    throw new Error('Unexpected response from the server.');
                }
                this.html = await res.text();
            } catch (e) {
                this.error = e && e.message ? e.message : 'Failed to load recent activity.';
            } finally {
                await minVisible;
                this.loading = false;
            }
        }
    };
}

// One number for a dashboard tile, from a Portal JSON proxy that answers { count }.
// Shows a dash if the call fails: the tile is a hint, not a report.
function summaryCount(url) {
    return {
        count: null,
        async load() {
            try {
                var r = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } });
                var data = r.ok ? await r.json() : null;
                this.count = data && typeof data.count === 'number' ? data.count : '\u2013';
            } catch (e) {
                this.count = '\u2013';
            }
        }
    };
}

// Reads a failure body the way the Portal writes it: JSON { error } from the API-unavailable
// filter and the JSON proxies, text/plain from partial endpoints, and a generic sentence for
// anything else (never an HTML error page).
async function portalErrorMessage(res, contentType) {
    var type = (contentType || res.headers.get('content-type') || '').toLowerCase();
    try {
        if (type.indexOf('application/json') !== -1) {
            var data = await res.json();
            if (data && (data.error || data.message)) return data.error || data.message;
        } else if (type.indexOf('text/plain') !== -1) {
            var text = await res.text();
            if (text) return text;
        }
    } catch (e) { /* fall through */ }
    if (res.status === 503 || res.status === 502 || res.status === 504) return 'The server is not reachable right now. Please try again in a moment.';
    return 'Request failed (' + res.status + ').';
}

// Copy and Download for a list of recovery codes. readCodes returns the codes at the moment of use,
// so the list is never held here. Spread it into a component that shows the codes.
function recoveryCodeTools(readCodes) {
    return {
        copied: false,
        failed: false,
        async copy() {
            try {
                await navigator.clipboard.writeText(readCodes().join('\n'));
                this.copied = true;
                this.failed = false;
                setTimeout(() => { this.copied = false; }, 2000);
            } catch (e) {
                // Clipboard access can be refused; the codes stay selectable and downloadable.
                this.failed = true;
            }
        },
        download() {
            const blob = new Blob([readCodes().join('\r\n') + '\r\n'], { type: 'text/plain' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'recovery-codes.txt';
            document.body.appendChild(link);
            link.click();
            link.remove();
            const href = link.href;
            setTimeout(() => URL.revokeObjectURL(href), 0);
        }
    };
}

// Relative time for feeds ("2h ago"), the exact time for its tooltip, and the antiforgery token
// every fetch POST sends. Shared by the notification bell and the case-notes timeline.
function portalRelativeTime(dateStr) {
    if (!dateStr) return '';
    var date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    var diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return diffMins + 'm ago';
    var diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return diffHours + 'h ago';
    var diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return diffDays + 'd ago';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function portalExactTime(dateStr) {
    if (!dateStr) return '';
    var date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function portalAntiForgeryToken() {
    var el = document.querySelector('input[name="__RequestVerificationToken"]');
    return el ? el.value : '';
}

// Back to top button
document.addEventListener('DOMContentLoaded', function () {
    var backToTop = document.getElementById('back-to-top');
    if (backToTop) {
        window.addEventListener('scroll', function () {
            if (document.body.scrollTop > 100 || document.documentElement.scrollTop > 100) {
                backToTop.style.display = 'block';
            } else {
                backToTop.style.display = 'none';
            }
        });
        backToTop.addEventListener('click', function () {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }
});